import type { Condition } from "@/hooks/useApi";
import { catalogConvention } from "@/lib/catalog/catalogConvention";
import { parsePrintedNumber } from "@/lib/catalog/collectorNumber";
import { splitPrintingName, withoutRepeatedCollectorNumber } from "@/lib/catalog/printingTreatment";

const ebayCardCondition: Record<Condition, string> = {
  NM: "Near Mint or Better",
  LP: "Lightly Played (Excellent)",
  MP: "Moderately Played (Very Good)",
  HP: "Heavily Played (Poor)",
  // eBay has no separate Damaged facet for collectible card games.
  DMG: "Heavily Played (Poor)",
};

export function ebayEdition(name: string, printing?: string | null) {
  if (/\b(?:1st|first)\s+edition\b/i.test(`${name} ${printing ?? ""}`)) return "first_edition";
  if (/\bunlimited\b/i.test(printing ?? "")) return "unlimited";
  return "unspecified";
}

/**
 * eBay sends a signed-out visitor to sign-in for any sold search (since Aug 2026), and the in-app
 * browser does not share Safari's eBay session, so the first search at a show lands on a sign-in.
 */
export const EBAY_SIGN_IN_HINT = "Sign in to eBay once to see sold prices";

/** Builds a user-initiated eBay sold-items search for the selected card and scope. */
export function ebaySoldsUrl({
  game,
  name,
  number,
  setName,
  printing,
  graded,
  condition,
  gradingCompany,
  grade,
}: {
  game?: string | null;
  name: string;
  number?: string | null;
  setName?: string | null;
  printing?: string | null;
  graded: boolean;
  condition?: Condition | null;
  gradingCompany?: string | null;
  grade?: string | null;
}): string {
  const grader = gradingCompany?.trim();
  const score = grade?.trim();
  const graderSearch = graded && grader && score
    ? grader.toUpperCase() === "BGS" || grader.toLowerCase() === "beckett"
      ? `("BGS ${score}","Beckett ${score}")`
      : `"${grader} ${score}"`
    : null;
  const otherGraders = graded && grader && !/^(PSA|BGS|Beckett)$/i.test(grader)
    ? GRADERS.filter((other) => other.toLowerCase() !== grader.toLowerCase())
    : [];
  // Measured on eBay sold results (2026-10-08): a treatment's words cost most of the comps where
  // the collector number already names the print (Umbreon VMAX 215/203: 762 sold, 116 with
  // "Alternate Art Secret"), while a stamp shares its base card's number and sellers write
  // "Stamp" or "Stamped". Magic sellers write the treatment more often than the number.
  const { base, treatments } = splitPrintingName(name);
  const pokemon = /^pokemon(-jp)?$/i.test(game?.trim() ?? "");
  const searchTreatments = treatments
    .filter((treatment) => !(pokemon && number && OWN_NUMBER_TREATMENT.test(treatment)))
    .map((treatment) => (/\bstamp/i.test(treatment) ? "(stamp,stamped)" : treatment));
  const searchNumber = !pokemon && catalogConvention(game).treatmentInName && treatments.length
    ? null
    : number;
  const cardName = [withoutRepeatedCollectorNumber(base, number), ...searchTreatments]
    .filter(Boolean).join(" ").replace(/\bholofoil\b/gi, "Holo");
  const printedNumber = parsePrintedNumber(number);
  const numberAlreadyNamed = !!printedNumber
    && cardName.split(/[\s(),-]+/).some((token) =>
      (!number?.includes("/") || token.includes("/"))
      && parsePrintedNumber(token)?.canonical === printedNumber.canonical
    );
  const printingTerm = printing?.trim().replace(/\bholofoil\b/gi, "Holo") ?? null;
  const edition = ebayEdition(cardName, printingTerm);
  const firstEdition = edition === "first_edition";
  const unlimited = edition === "unlimited";
  const editionPrinting = printingTerm
    ?.replace(/\bunlimited\b/gi, "")
    .replace(/\b(?:1st|first)\s+edition\b/gi, "")
    .trim();
  // An exclusion never drops a word the card itself uses (TAG TEAM, ACE SPEC), including a
  // treatment left out of the search.
  const exclude = (terms: string[]) => {
    const own = `${name} ${printing ?? ""}`.toLowerCase().split(/[^a-z0-9]+/);
    const kept = terms.filter((term) => !own.includes(term.toLowerCase()));
    return kept.length ? `-(${kept.join(",")})` : null;
  };
  const query = [
    cardName,
    searchNumber && !numberAlreadyNamed ? searchNumber : null,
    number ? null : setName ?? game,
    firstEdition && !/\b(?:1st|first)\s+edition\b/i.test(cardName) ? "1st Edition" : null,
    editionPrinting && !/^(normal|standard|regular)$/i.test(editionPrinting)
      && !(graded && /^holo$/i.test(editionPrinting))
      && !cardName.toLowerCase().includes(editionPrinting.toLowerCase())
      ? editionPrinting
      : null,
    graderSearch,
    otherGraders.length ? exclude(otherGraders) : null,
    unlimited ? exclude(["1st", "First"]) : null,
    // Without a condition there is no ungraded-only aspect filter, so slabs would price a raw copy
    // (17 of 60 sold for a raw Charizard ex 199/165).
    !graded && !condition ? exclude(GRADERS) : null,
    exclude(NOT_THE_CARD),
  ].filter((part): part is string => !!part?.trim()).join(" ");
  // Trading-card singles only, most recently sold first.
  const params = new URLSearchParams({
    _nkw: query,
    _sacat: CCG_INDIVIDUAL_CARDS,
    LH_Sold: "1",
    LH_Complete: "1",
    _sop: "13",
  });
  if (!graded && condition) {
    // eBay's card-condition aspect key and value are encoded twice in search links.
    params.set(
      "Card%20Condition",
      encodeURIComponent(ebayCardCondition[condition]).replaceAll("(", "%28").replaceAll(
        ")",
        "%29",
      ),
    );
  }
  return `https://www.ebay.com/sch/i.html?${params.toString()}`;
}

/** eBay's "CCG Individual Cards" category. */
const CCG_INDIVIDUAL_CARDS = "183454";

const GRADERS = ["PSA", "BGS", "Beckett", "CGC", "SGC", "TAG", "ACE"];

/** Sold listings that share a card's title but are not a copy of it. */
const NOT_THE_CARD = ["proxy", "custom", "orica", "choose"];

/** Pokémon treatments printed under their own collector number, which already names them. */
const OWN_NUMBER_TREATMENT = /\b(full art|alt art|alternate|secret|shiny)\b/i;

/** The same-site page a web click goes through on its way to eBay (`app/out/ebay-solds.tsx`). */
export const EBAY_SOLDS_FORWARD_PATH = "/out/ebay-solds";

/** The only query keys `ebaySoldsUrl` writes, so the forward cannot be steered anywhere else. */
const FORWARDED_KEYS = [
  "_nkw",
  "_sacat",
  "LH_Sold",
  "LH_Complete",
  "_sop",
  "Card%20Condition",
  // Written by links built before `_sacat` (TRO-1453 era), which may still be shared.
  "_dcat",
];

/**
 * The web link for a sold search: a same-site page that forwards to it.
 *
 * eBay's app claims every `ebay.com/sch/*` link (its apple-app-site-association), and drops the
 * Sold/Completed filters it is handed (TRO-1453), so a direct tap in iPhone Safari with the app
 * installed showed live asking prices as if they were sold ones. iOS opens a claimed app for a
 * tap that crosses domains, not for a same-site tap or a redirect the page makes itself, so the
 * click lands here first and the page forwards in Safari. Native already avoids the app by opening
 * an in-app browser.
 */
export function ebaySoldsForwardPath(url: string): string {
  return `${EBAY_SOLDS_FORWARD_PATH}?${url.split("?")[1] ?? ""}`;
}

/**
 * The eBay sold search a forward page was handed, rebuilt from its query alone: only the keys
 * `ebaySoldsUrl` writes, always to eBay's search, always Sold and Completed. So the page is never
 * an open redirect, and a hand-edited link still opens sold results.
 */
export function ebaySoldsUrlFromQuery(search: string): string {
  const given = new URLSearchParams(search);
  const params = new URLSearchParams();
  for (const [key, value] of given) {
    if (FORWARDED_KEYS.includes(key)) params.append(key, value);
  }
  params.set("LH_Sold", "1");
  params.set("LH_Complete", "1");
  return `https://www.ebay.com/sch/i.html?${params.toString()}`;
}
